import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SEOHead } from "@/components/seo-head";
import { NavigationHeader } from "@/components/navigation-header";
import { Calculator, Send, CheckCircle } from "lucide-react";

interface QuoteFormData {
  fullName: string;
  email: string;
  phone: string;
  companyName: string;
  website: string;
  currentSituation: string;
  services: string[];
  budget: string;
  timeline: string;
  comments: string;
}

const QuoteRequest = () => {
  const [formData, setFormData] = useState<QuoteFormData>({
    fullName: "",
    email: "",
    phone: "",
    companyName: "",
    website: "",
    currentSituation: "",
    services: [],
    budget: "",
    timeline: "",
    comments: "",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const serviceOptions = [
    { id: "seo", label: "SEO" },
    { id: "social", label: "Redes Sociales" },
    { id: "branding", label: "Branding" },
    { id: "advertising", label: "Publicidad" },
    { id: "web", label: "Desarrollo Web" },
    { id: "other", label: "Otro" },
  ];

  const budgetOptions = [
    { value: "less-1000", label: "Menos de 1.000€" },
    { value: "1000-3000", label: "1.000€ - 3.000€" },
    { value: "3000-5000", label: "3.000€ - 5.000€" },
    { value: "more-5000", label: "Más de 5.000€" },
  ];

  const handleServiceChange = (serviceId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      services: checked
        ? [...prev.services, serviceId]
        : prev.services.filter(s => s !== serviceId)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.fullName.trim() || !formData.email.trim()) {
      toast({
        title: "Error",
        description: "Por favor, completa los campos obligatorios.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke('send-quote-request', {
        body: {
          ...formData,
          services: formData.services.map(serviceId => 
            serviceOptions.find(opt => opt.id === serviceId)?.label || serviceId
          ).join(", "),
        }
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "¡Solicitud enviada!",
        description: "Nuestro equipo se pondrá en contacto contigo en menos de 24 horas.",
      });
      
    } catch (error) {
      console.error('Error sending quote request:', error);
      toast({
        title: "Error",
        description: "Ha ocurrido un error al enviar tu solicitud. Por favor, inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background relative">{/* Added relative */}
        <NavigationHeader />{/* Added navigation */}
        <SEOHead 
          title="Solicitud Enviada - MKTN GUARDIANS"
          description="Tu solicitud de presupuesto personalizado ha sido enviada correctamente."
        />
        
        <div className="container mx-auto px-4 py-16 pt-20 sm:pt-24">{/* Added top padding */}
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8">
              <CheckCircle className="h-24 w-24 text-primary mx-auto mb-6" />
              <h1 className="text-4xl font-bold text-foreground mb-4">
                ¡Gracias por tu solicitud!
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Nuestro equipo se pondrá en contacto contigo en menos de 24 horas.
              </p>
              <div className="bg-card border border-border rounded-lg p-6">
                <p className="text-foreground">
                  Hemos enviado una confirmación a tu email. 
                  Mientras tanto, puedes explorar nuestros servicios o agendar una consultoría.
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <Button asChild className="w-full sm:w-auto">
                <a href="/reservar-cita">Agendar Consultoría</a>
              </Button>
              <Button variant="outline" asChild className="w-full sm:w-auto ml-0 sm:ml-4">
                <a href="/">Volver al Inicio</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">{/* Added relative */}
      <NavigationHeader />{/* Added navigation */}
      <SEOHead 
        title="Presupuesto Personalizado - MKTN GUARDIANS"
        description="Solicita un presupuesto personalizado para tu estrategia de marketing digital. Especialistas en placas solares y reformas."
      />
      
      <div className="container mx-auto px-4 py-16 pt-20 sm:pt-24">{/* Added top padding */}
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="bg-primary/10 p-4 rounded-full">
                <Calculator className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Presupuesto Personalizado
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Cuéntanos sobre tu proyecto y te prepararemos una propuesta detallada 
              adaptada a tus necesidades específicas.
            </p>
          </div>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-2xl text-foreground">
                Información del Proyecto
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Información Personal */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-foreground">
                      Nombre completo *
                    </Label>
                    <Input
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                      placeholder="Tu nombre completo"
                      required
                      className="bg-input border-border text-foreground"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-foreground">
                      Email de contacto *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="tu@email.com"
                      required
                      className="bg-input border-border text-foreground"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-foreground">
                      Teléfono
                    </Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+34 XXX XXX XXX"
                      className="bg-input border-border text-foreground"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="companyName" className="text-foreground">
                      Nombre de la empresa
                    </Label>
                    <Input
                      id="companyName"
                      value={formData.companyName}
                      onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                      placeholder="Nombre de tu empresa"
                      className="bg-input border-border text-foreground"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website" className="text-foreground">
                    Página web de la empresa (si tiene)
                  </Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://tuempresa.com"
                    className="bg-input border-border text-foreground"
                  />
                </div>

                {/* Situación Actual */}
                <div className="space-y-2">
                  <Label htmlFor="currentSituation" className="text-foreground">
                    Estado actual de la empresa / marketing
                  </Label>
                  <Textarea
                    id="currentSituation"
                    value={formData.currentSituation}
                    onChange={(e) => setFormData(prev => ({ ...prev, currentSituation: e.target.value }))}
                    placeholder="Describe la situación actual de tu empresa, qué estrategias de marketing usas actualmente, qué problemas tienes..."
                    className="bg-input border-border text-foreground min-h-[100px]"
                  />
                </div>

                {/* Servicios */}
                <div className="space-y-4">
                  <Label className="text-foreground text-lg">
                    Servicios que necesitas
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {serviceOptions.map((service) => (
                      <div key={service.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={service.id}
                          checked={formData.services.includes(service.id)}
                          onCheckedChange={(checked) => 
                            handleServiceChange(service.id, checked as boolean)
                          }
                        />
                        <Label htmlFor={service.id} className="text-foreground">
                          {service.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Presupuesto y Plazo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="budget" className="text-foreground">
                      Presupuesto estimado
                    </Label>
                    <Select 
                      value={formData.budget} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, budget: value }))}
                    >
                      <SelectTrigger className="bg-input border-border text-foreground">
                        <SelectValue placeholder="Selecciona un rango" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        {budgetOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="timeline" className="text-foreground">
                      Plazo esperado
                    </Label>
                    <Input
                      id="timeline"
                      value={formData.timeline}
                      onChange={(e) => setFormData(prev => ({ ...prev, timeline: e.target.value }))}
                      placeholder="Ej: 3 meses, ASAP, etc."
                      className="bg-input border-border text-foreground"
                    />
                  </div>
                </div>

                {/* Comentarios */}
                <div className="space-y-2">
                  <Label htmlFor="comments" className="text-foreground">
                    Comentarios adicionales
                  </Label>
                  <Textarea
                    id="comments"
                    value={formData.comments}
                    onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                    placeholder="Cualquier información adicional que consideres relevante..."
                    className="bg-input border-border text-foreground min-h-[100px]"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                        Enviando solicitud...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Enviar Solicitud de Presupuesto
                      </>
                    )}
                  </Button>
                  
                  <p className="text-center text-sm text-muted-foreground mt-4">
                    Al enviar este formulario, aceptas que contactemos contigo para proporcionarte 
                    un presupuesto personalizado.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default QuoteRequest;